import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    "bg-linear-to-r from-gray-950 to-gray-800 text-white hover:from-gray-900 hover:to-gray-700 shadow-sm hover:shadow-md border border-gray-800/10 active:scale-[0.98]",
  secondary:
    "bg-white/80 backdrop-blur-xs text-gray-800 border border-gray-200/80 hover:bg-gray-50/95 hover:text-gray-950 shadow-xs hover:shadow-sm active:scale-[0.98]",
  ghost:
    "bg-transparent text-gray-600 hover:bg-gray-100/60 hover:text-gray-950 active:scale-[0.98]",
  danger:
    "bg-linear-to-r from-red-600 to-rose-500 text-white hover:from-red-700 hover:to-rose-600 shadow-xs hover:shadow-sm active:scale-[0.98]",
};

const sizes: Record<Size, string> = {
  sm: "h-8.5 px-3.5 text-xs rounded-md",
  md: "h-10 px-5 text-sm rounded-lg",
  lg: "h-11.5 px-6 text-base rounded-xl",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "primary", size = "md", loading, disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors whitespace-nowrap cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed",
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" className="opacity-75" />
        </svg>
      )}
      {children}
    </button>
  );
});
